import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addWallet, getCrypto, getNfts, removeWallet, renameWallet, syncCrypto, updateNft } from '../../data/crypto';
import PortfolioTabs from '../portfolio/PortfolioTabs';
import PortfolioHeader from '../portfolio/PortfolioHeader';
import ViewToggle from '../portfolio/ViewToggle';
import CryptoTable from '../portfolio/CryptoTable';
import CryptoWallets, { walletName } from '../portfolio/CryptoWallets';
import NftTable from '../portfolio/NftTable';
import SectionSummary from '../portfolio/SectionSummary';
import useSummaryInputs from '../portfolio/useSummaryInputs';
import { Skeleton } from '../portfolio/Loading';
import { startInputKey } from '../portfolio/sections';
import { useT } from '../../i18n';

// On-chain holdings across tracked wallets on Ethereum, Arbitrum, Base,
// Solana and Hyperliquid, and the NFTs in the EVM wallets.
//
// Live, unlike the Total Portfolio, which is as of its last rebuild. The
// summary splits Coins from NFTs; the coins' total put in is entered by hand
// until the ledger can be trusted (see CRYPTO_PNL_WIP).
//
// The history behind the entry prices is indexed in bounded runs — see
// `syncCrypto`. The page starts one in the background whenever a wallet is
// behind, and the table fills in its averages when it lands. Balances and
// prices on the page are live regardless.

const LOAD_STATE = { LOADING: 'loading', READY: 'ready', ERROR: 'error' };
const ALL = 'all';

const round2 = (v) => Math.round(v * 100) / 100;

/** A collection narrowed to one wallet's items, its totals recomputed over
 *  them — the server's are over every wallet. Same rules as the server's
 *  `nfts.ts`: a sum of the known figures, null only when none is known. */
function inWallet(collection, walletId) {
    const items = collection.items.filter((i) => i.walletId === walletId);
    const total = (field) => {
        const known = items.filter((i) => i[field] !== null && i[field] !== undefined);
        return {
            sum: known.length === 0 ? null : round2(known.reduce((s, i) => s + i[field], 0)),
            unknown: items.length - known.length,
        };
    };
    const cost = total('costUsd');
    const value = total('valueUsd');
    return {
        ...collection,
        items,
        costUsd: cost.sum,
        valueUsd: value.sum,
        unknownCost: cost.unknown,
        unknownValue: value.unknown,
        wallets: items.length > 0 ? 1 : 0,
    };
}

/** A wallet synced longer ago than this is caught up on the next visit. */
const STALE_MS = 30 * 60 * 1000;
/** Twenty-second runs; this is ten minutes of backfill before giving up. */
const MAX_RUNS = 30;

/** A catalogue key for a failed request — never the server's English text. */
const errorKey = (err, fallback) => {
    if (err?.status === 401) return 'crypto.errors.session';
    if (err?.status === 0) return 'crypto.errors.offline';
    if (err?.code === 'badAddress') return 'crypto.errors.badAddress';
    if (err?.code === 'duplicate') return 'crypto.errors.duplicate';
    if (err?.status === 400) return 'crypto.errors.invalid';
    return fallback;
};

const needsSync = (w) =>
    w.configured && (!w.historyComplete || !w.syncedAt || Date.now() - new Date(w.syncedAt) > STALE_MS);

export default function CryptoHoldings() {
    const { t, tn, fmt } = useT();
    const [state, setState] = useState(LOAD_STATE.LOADING);
    const [data, setData] = useState(null);
    const [scope, setScope] = useState(ALL);
    const [syncing, setSyncing] = useState(false);
    const [notice, setNotice] = useState(null);
    const [walletError, setWalletError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [nfts, setNfts] = useState(null);
    const { inputs, setInput } = useSummaryInputs();
    const syncingRef = useRef(false);
    const syncAgainRef = useRef(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const loadNfts = useCallback(async () => {
        try {
            const result = await getNfts();
            if (mounted.current) setNfts(result);
            return result;
        } catch {
            // The coins still stand on their own.
            return null;
        }
    }, []);

    const load = useCallback(async () => {
        // Independent reads: neither waits on the other. A failed NFT read
        // resolves to null inside loadNfts, so only the coins can fail this.
        const [result, nftResult] = await Promise.all([getCrypto(), loadNfts()]);
        if (!mounted.current) return null;
        setData(result);
        setState(LOAD_STATE.READY);
        // A wallet whose NFTs have never been through a sync.
        return { ...result, nftsPending: Boolean(nftResult?.pending) };
    }, [loadNfts]);

    // One sync at a time per tab. A second concurrent one would be harmless
    // to the data (inserts are idempotent) but doubles the provider calls.
    //
    // A request that arrives MID-sync is not dropped, it is queued: the sync
    // runs again once this one finishes. Dropping it lost wallets. Five added
    // in quick succession left the last two never indexed — the run in flight
    // had listed the wallets before they existed, finished `done`, and the
    // requests their adds made had been turned away at this door.
    const sync = useCallback(async () => {
        if (syncingRef.current) {
            syncAgainRef.current = true;
            return;
        }
        syncingRef.current = true;
        setSyncing(true);
        setNotice(null);
        try {
            let runs = 0;
            do {
                syncAgainRef.current = false;
                for (; runs < MAX_RUNS && mounted.current; runs += 1) {
                    const { done } = await syncCrypto();
                    if (done) break;
                }
            } while (syncAgainRef.current && runs < MAX_RUNS && mounted.current);
            await load();
        } catch (err) {
            if (mounted.current) setNotice(errorKey(err, 'crypto.errors.sync'));
        } finally {
            syncingRef.current = false;
            if (mounted.current) setSyncing(false);
        }
    }, [load]);

    useEffect(() => {
        load()
            .then((result) => { if (result?.wallets?.some(needsSync) || result?.nftsPending) sync(); })
            .catch((err) => {
                if (!mounted.current) return;
                setNotice(errorKey(err, 'crypto.errors.load'));
                setState(LOAD_STATE.ERROR);
            });
    }, [load, sync]);

    const add = async (form) => {
        setBusy(true);
        setWalletError(null);
        try {
            await addWallet(form);
            await load();
            sync();
            return true;
        } catch (err) {
            setWalletError(errorKey(err, 'crypto.errors.save'));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const rename = async (id, label) => {
        setBusy(true);
        setWalletError(null);
        try {
            await renameWallet(id, label);
            await load();
            return true;
        } catch (err) {
            setWalletError(errorKey(err, 'crypto.errors.rename'));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        setBusy(true);
        setWalletError(null);
        try {
            await removeWallet(id);
            await load();
        } catch (err) {
            setWalletError(errorKey(err, 'crypto.errors.remove'));
        } finally {
            setBusy(false);
        }
    };

    const wallets = useMemo(() => data?.wallets ?? [], [data]);
    const walletNames = useMemo(() => new Map(wallets.map((w) => [w.id, walletName(w)])), [wallets]);
    const active = scope === ALL || wallets.some((w) => w.id === scope) ? scope : ALL;
    const rows = useMemo(
        () => (data?.rows ?? []).filter((r) => active === ALL || r.walletId === active),
        [data, active],
    );

    const total = rows.reduce((sum, r) => sum + r.value, 0);
    // NFTs in the wallet in view, or all of them.
    const collections = useMemo(() => (nfts?.collections ?? [])
        .map((c) => (active === ALL ? c : inWallet(c, active)))
        .filter((c) => c.items.length > 0), [nfts, active]);
    const nftItems = collections.flatMap((c) => c.items);
    const has = (v) => v !== null && v !== undefined;
    // As the snapshot counts them: value over every item that has one, cost
    // and P/L over the items where both are known.
    const nftValue = nftItems.filter((i) => has(i.valueUsd)).reduce((s, i) => s + i.valueUsd, 0);
    const bothKnown = nftItems.filter((i) => has(i.valueUsd) && has(i.costUsd));
    const nftCost = bothKnown.reduce((s, i) => s + i.costUsd, 0);
    const nftPnl = bothKnown.reduce((s, i) => s + i.valueUsd - i.costUsd, 0);
    // The typed-in total put in covers every wallet, so it only applies to All.
    const coinStart = inputs[startInputKey('crypto')] ?? null;
    const parts = [
        {
            key: 'crypto',
            labelKey: 'sections.coins',
            inputKey: startInputKey('crypto'),
            windows: { all: { start: coinStart, current: total, fromDate: null } },
        },
        ...(nftItems.length > 0 ? [{
            key: 'nft',
            labelKey: 'sections.nfts',
            windows: {
                all: { start: nftCost, current: nftValue, pnl: nftPnl, fromDate: null },
            },
        }] : []),
    ];

    const saveNft = async (id, values) => {
        try {
            await updateNft(id, values);
            await loadNfts();
            return true;
        } catch {
            return false;
        }
    };
    const indexing = wallets.some((w) => w.configured && !w.historyComplete);

    return (
        <section className="holdings__sec">
            <div className="wrapper">
                <PortfolioHeader
                    title={t('crypto.title')}
                    status={data?.asOf && t('common.asOf', { when: fmt.dateTime(data.asOf) })}
                    onRefresh={sync}
                    refreshing={syncing}
                />

                <PortfolioTabs />

                {state === LOAD_STATE.LOADING && <Skeleton rows={8} />}

                {state === LOAD_STATE.ERROR && (
                    <p className="holdings__error" role="alert">{t(notice ?? 'crypto.errors.load')}</p>
                )}

                {state === LOAD_STATE.READY && data && (
                    <>
                        {wallets.length > 1 && (
                            <div className="holdings__scope">
                                <ViewToggle
                                    ariaLabel={t('crypto.scopeAria')}
                                    options={[[ALL, t('common.all')], ...wallets.map((w) => [w.id, walletName(w)])]}
                                    value={active}
                                    onChange={setScope}
                                />
                            </div>
                        )}

                        {(rows.length > 0 || nftItems.length > 0) && (
                            <>
                                <div className="holdings__total">
                                    <span className="holdings__total_value">{fmt.number(total + nftValue)}</span>
                                    <span className="holdings__total_currency">USD</span>
                                </div>
                                {/* The coins' typed-in total put in covers every
                                    wallet, so one wallet alone has no start to
                                    show: just its value above. */}
                                {active === ALL && (
                                    <SectionSummary
                                        parts={parts}
                                        windowKey="all"
                                        inputs={inputs}
                                        onInput={setInput}
                                        showParts
                                    />
                                )}
                            </>
                        )}

                        {notice && <p className="holdings__error" role="alert">{t(notice)}</p>}
                        {(syncing || indexing) && (
                            <p className="crypto__indexing" role="status">{t('crypto.indexing')}</p>
                        )}

                        {rows.length > 0 ? (
                            <CryptoTable
                                rows={rows}
                                walletNames={walletNames}
                                showWallet={active === ALL && wallets.length > 1}
                            />
                        ) : wallets.length > 0 && (
                            <div className="holdings__empty">
                                <p className="desc">{t('crypto.empty')}</p>
                            </div>
                        )}

                        {data.hidden > 0 && (
                            <p className="crypto__hidden">{tn('crypto.hidden', data.hidden)}</p>
                        )}

                        {collections.length > 0 && (
                            <>
                                <h2 className="notes__h">{t('sections.nfts')}</h2>
                                <NftTable collections={collections} onSave={saveNft} busy={busy} />
                            </>
                        )}

                        <CryptoWallets
                            wallets={wallets}
                            onAdd={add}
                            onRename={rename}
                            onRemove={remove}
                            busy={busy}
                            error={walletError}
                        />
                    </>
                )}
            </div>
        </section>
    );
}
